import { Link } from "react-router-dom";
import { SiTether, SiEthereum, SiX, SiTelegram, SiWhatsapp } from "react-icons/si";
import SkalaLogo from "./SkalaLogo";
import TrustpilotWidget from "./TrustpilotWidget";
import { getLocalizedBlogPosts } from "../data/blogMultilingual";
import { useSiteSettings } from "../contexts/SiteSettingsContext";
import { useLanguage } from "../contexts/LanguageContext";

const groupsFor = (lang, blogPosts = []) => {
    const l = {
        en: {
            platforms: "Platforms", resources: "Resources", company: "Company", allResources: "All Resources", pricing: "Pricing", faq: "FAQ", apply: "Apply for Access", login: "Sign in", privacy: "Privacy Policy", terms: "Service Agreement", refund: "Refund Policy", contact: "Contact",
        },
        pt: {
            platforms: "Plataformas", resources: "Recursos", company: "Empresa", allResources: "Todos os Recursos", pricing: "Preços", faq: "FAQ", apply: "Solicitar Acesso", login: "Entrar", privacy: "Política de Privacidade", terms: "Termos de Serviço", refund: "Política de Reembolso", contact: "Contato",
        },
        es: {
            platforms: "Plataformas", resources: "Recursos", company: "Empresa", allResources: "Todos los Recursos", pricing: "Precios", faq: "FAQ", apply: "Solicitar Acceso", login: "Iniciar sesión", privacy: "Política de Privacidad", terms: "Términos de Servicio", refund: "Política de Reembolso", contact: "Contacto",
        },
        ru: {
            platforms: "Платформы", resources: "Ресурсы", company: "Компания", allResources: "Все материалы", pricing: "Тарифы", faq: "FAQ", apply: "Подать заявку", login: "Войти", privacy: "Политика конфиденциальности", terms: "Пользовательское соглашение", refund: "Политика возврата", contact: "Контакты",
        },
        tr: {
            platforms: "Platformlar", resources: "Kaynaklar", company: "Şirket", allResources: "Tüm Kaynaklar", pricing: "Fiyatlar", faq: "SSS", apply: "Erişim Başvurusu", login: "Giriş Yap", privacy: "Gizlilik Politikası", terms: "Hizmet Sözleşmesi", refund: "İade Politikası", contact: "İletişim",
        },
    }[lang] || {
        platforms: "Platforms", resources: "Resources", company: "Company", allResources: "All Resources", pricing: "Pricing", faq: "FAQ", apply: "Apply for Access", login: "Sign in", privacy: "Privacy Policy", terms: "Service Agreement", refund: "Refund Policy", contact: "Contact",
    };

    return [
        {
            title: l.platforms,
            links: [
                { label: "Meta", to: "/platforms/meta" },
                { label: "Google", to: "/platforms/google" },
                { label: "TikTok", to: "/platforms/tiktok" },
                { label: "Snapchat", to: "/platforms/snapchat" },
                { label: "Microsoft (Bing)", to: "/platforms/bing" },
                { label: "Taboola", to: "/platforms/taboola" },
                { label: "Outbrain", to: "/platforms/outbrain" },
            ],
        },
        {
            title: l.resources,
            links: [
                { label: l.allResources, to: "/blog" },
                ...blogPosts.slice(0, 4).map((p) => ({
                    label: p.title,
                    to: `/blog/${p.slug}`,
                    testIdSuffix: `blog-${p.slug}`,
                })),
            ],
        },
        {
            title: l.company,
            links: [
                { label: l.pricing, href: "/#pricing" },
                { label: l.faq, href: "/#faq" },
                { label: l.apply, to: "/signup" },
                { label: l.login, to: "/login" },
                { label: l.privacy, to: "/privacy" },
                { label: l.terms, to: "/terms" },
                { label: l.refund, to: "/refund" },
                { label: l.contact, to: "/contact" },
            ],
        },
    ];
};

const socials = (settings) => [
    {
        Icon: SiTelegram,
        href: settings.contact_telegram || "https://t.me/adrise",
        label: "Telegram",
        color: "text-[#229ED9]",
    },
    {
        Icon: SiWhatsapp,
        href: settings.contact_whatsapp || "https://wa.me/0",
        label: "WhatsApp",
        color: "text-[#25D366]",
    },
    {
        Icon: SiX,
        href: "https://x.com/adrise",
        label: "X / Twitter",
        color: "text-foreground",
    },
];

export default function Footer() {
    const { settings } = useSiteSettings();
    const { language } = useLanguage();
    const blogPosts = getLocalizedBlogPosts(language);
    const socialLinks = socials(settings);
    const groups = groupsFor(language, blogPosts);
    const bottomRights = {
        en: "All rights reserved.",
        pt: "Todos os direitos reservados.",
        es: "Todos los derechos reservados.",
        ru: "Все права защищены.",
        tr: "Tüm hakları saklıdır.",
    }[language] || "All rights reserved.";
    const fundingLabel = {
        en: "Funding",
        pt: "Financiamento",
        es: "Financiación",
        ru: "Финансирование",
        tr: "Fonlama",
    }[language] || "Funding";
    const intro = {
        en: "Premium Platinum HIVA Partner agency ad accounts. Built for teams scaling with Credit Line and crypto top-ups.",
        pt: "Contas premium de anúncios com selo Platinum HIVA Partner. Feito para times que escalam com Credit Line e recargas em cripto.",
        es: "Cuentas premium de agencia con nivel Platinum HIVA Partner. Diseñado para equipos que escalan con Credit Line y recargas en cripto.",
        ru: "Премиальные агентские аккаунты уровня Platinum HIVA Partner. Для команд, которые масштабируются через Credit Line и пополнения в крипто.",
        tr: "Premium Platinum HIVA Partner ajans reklam hesapları. Credit Line ve kripto top-up ile ölçeklenen ekipler için tasarlandı.",
    }[language];

    return (
        <footer
            data-testid="site-footer"
            className="relative border-t border-white/5 bg-background"
        >
            <div className="container-skala py-16">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-12">
                    <div className="lg:col-span-3 space-y-5">
                        <SkalaLogo size="lg" />
                        <p className="text-sm text-muted-foreground max-w-xs leading-relaxed">
                            {intro}
                        </p>

                        <div className="pt-2">
                            <TrustpilotWidget variant="mini" />
                        </div>

                        <div className="flex items-center gap-3 pt-2">
                            <span className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                                {fundingLabel}
                            </span>
                            <div className="flex items-center gap-3 text-muted-foreground">
                                <SiTether className="w-4 h-4" title="USDT (TRC-20 / ERC-20)" />
                                <SiEthereum className="w-4 h-4" title="USDC (ERC-20)" />
                            </div>
                        </div>

                        <div className="flex items-center gap-3 pt-2">
                            {socialLinks.map((s) => (
                                <a
                                    key={s.label}
                                    href={s.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={s.label}
                                    data-testid={`footer-social-${s.label.toLowerCase().replace(/[\s/]+/g, "-")}`}
                                    className={`grid place-items-center w-9 h-9 rounded-lg bg-white/[0.02] border border-white/10 hover:border-white/20 hover:bg-white/[0.05] transition-colors ${s.color}`}
                                >
                                    <s.Icon className="w-4 h-4" />
                                </a>
                            ))}
                        </div>
                    </div>

                    {groups.map((g) => (
                        <div
                            key={g.title}
                            className="lg:col-span-3 space-y-4"
                            data-testid={`footer-group-${g.title.toLowerCase()}`}
                        >
                            <h4 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                                {g.title}
                            </h4>
                            <ul className="space-y-3">
                                {g.links.map((l) =>
                                    l.to ? (
                                        <li key={l.label}>
                                            <Link
                                                to={l.to}
                                                data-testid={`footer-link-${(l.testIdSuffix || l.label).toLowerCase().replace(/[^a-z0-9-]+/g, "-")}`}
                                                className="text-sm text-foreground/80 hover:text-foreground transition-colors line-clamp-1"
                                            >
                                                {l.label}
                                            </Link>
                                        </li>
                                    ) : (
                                        <li key={l.label}>
                                            <a
                                                href={l.href}
                                                data-testid={`footer-link-${(l.testIdSuffix || l.label).toLowerCase().replace(/[^a-z0-9-]+/g, "-")}`}
                                                className="text-sm text-foreground/80 hover:text-foreground transition-colors"
                                            >
                                                {l.label}
                                            </a>
                                        </li>
                                    ),
                                )}
                            </ul>
                        </div>
                    ))}
                </div>

                <div className="mt-14 pt-8 border-t border-white/5 flex flex-col md:flex-row gap-4 justify-between text-xs text-muted-foreground">
                    <p>© {new Date().getFullYear()} Adrise Agency. {bottomRights}</p>
                    <p className="font-mono tracking-wide">
                        {({ tr: "Sadece kripto", es: "Solo cripto", pt: "Só cripto", ru: "Только крипто" })[language] || "Crypto only"} · USDT (TRC-20 / ERC-20) · USDC (ERC-20)
                    </p>
                </div>
            </div>
        </footer>
    );
}
